// Screen on/off schedules and layout rotation (Settings → Screens → Schedules).
// Schedules are in each screen's own local time: "07:00–19:00 on weekdays" means 7 am where
// the screen is. A period whose end is at or before its start runs past midnight into the
// next day (22:00–02:00), and 00:00–00:00 is the whole day.

import type { ScreenRotation, ScreenSchedule } from '../services/api';

const minutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Is a screen on this schedule on at `at`? No schedule = always on. */
export function isOn(schedule: ScreenSchedule | null, at: Date): boolean {
  if (!schedule) return true;
  const day = at.getDay();
  const m = at.getHours() * 60 + at.getMinutes();
  const today = schedule.days[day] ?? [];
  const yesterday = schedule.days[(day + 6) % 7] ?? [];
  for (const p of today) {
    const from = minutes(p.from);
    const to = minutes(p.to);
    if (to > from ? m >= from && m < to : m >= from) return true;
  }
  // Yesterday's overnight periods carry on until their end time today.
  return yesterday.some((p) => minutes(p.to) <= minutes(p.from) && m < minutes(p.to));
}

/** When the screen next switches on or off (to the minute), within a week; null = never. */
export function nextChange(schedule: ScreenSchedule | null, from: Date): Date | null {
  if (!schedule) return null;
  const now = isOn(schedule, from);
  const t = new Date(from);
  t.setSeconds(0, 0);
  for (let i = 0; i < 8 * 1440; i++) {
    t.setMinutes(t.getMinutes() + 1);
    if (isOn(schedule, t) !== now) return t;
  }
  return null;
}

/** Hours the schedule is on in a week, for its summary. */
export function weeklyHours(schedule: ScreenSchedule): number {
  let on = 0;
  const t = new Date(2026, 0, 4); // a Sunday
  for (let i = 0; i < 7 * 1440; i++) {
    if (isOn(schedule, t)) on++;
    t.setMinutes(t.getMinutes() + 1);
  }
  return Math.round(on / 6) / 10;
}

/**
 * What a rotating screen shows at `at`: the clock decides (not a timer), so screens with the
 * same rotation stay in step and a reload carries on where it was. 'main' = the main setup.
 */
export function rotationItem(rotation: ScreenRotation, at: number): string {
  const n = rotation.items.length;
  const step = Math.floor(at / (rotation.intervalSec * 1000));
  return rotation.items[((step % n) + n) % n]!;
}

/** Milliseconds until the rotation moves on. */
export function msToNextRotation(rotation: ScreenRotation, at: number): number {
  const period = rotation.intervalSec * 1000;
  return period - (at % period);
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
